/**
 * Governance Programs — live list of rolled-out SOP walkthrough programs.
 *
 * Provides the missing "Programs" tab next to SOPs. Also exposes a Roll Out
 * entry point (the admin picks any existing SOP via dropdown plus variant /
 * maker / checker / geography / dates) so we don't need to edit the SOP tab
 * (which still renders the mock component).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Search, Calendar, User, Plus, Rocket } from 'lucide-react';
import FormControl from '../common/FormControl';
import Skeleton from '../common/Skeleton';
import Modal from '../common/Modal';
import Button from '../common/Button';
import Pagination from '../common/Pagination';
import Toast, { ToastMessage } from '../common/Toast';
import NoDataFound from '../common/NoDataFound';
import GeographyPicker, { GeographySelection, isGeographyComplete } from '../common/GeographyPicker';
import sopProgramService, { SopProgram, SopVariant } from '../../services/sopProgramService';
import { governanceProcessesService } from '../../services/governanceService';
import { userService } from '../../services/userService';
import extractApiError from '../../utils/apiError';
import { usePermissions, usePermission } from '../../contexts/PermissionContext';
import { useEntitySettings } from '../../contexts/EntitySettingsContext';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { ValidatedDatePicker } from '../../validation/ValidatedFields';

// Map program status to the app-canonical .status-badge semantic variant
// (global.css). Keeps PAT programs visually consistent with every other
// status pill in the app instead of ad-hoc Tailwind colours.
const statusBadgeVariant = (status?: string | null) => {
    switch (status) {
        case 'Complete':    return 'success';
        case 'In-Progress': return 'info';
        case 'Not-Started': return 'warning';
        default:            return 'neutral';
    }
};

interface SopOption { id: number; process_code?: string; process_name: string; }

const SopRolloutModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    onRolledOut: () => void;
    onError: (msg: string) => void;
}> = ({ isOpen, onClose, onRolledOut, onError }) => {
    const [sops, setSops] = useState<SopOption[]>([]);
    const [users, setUsers] = useState<{ id: number; name: string }[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [sopId, setSopId] = useState('');
    const [variant, setVariant] = useState<SopVariant>('Compliance');
    const [makerId, setMakerId] = useState('');
    const [checkerId, setCheckerId] = useState('');
    const [geography, setGeography] = useState<GeographySelection>({ mode: 'locations', location_ids: [] });
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const vh = useFieldValidation();

    // Keep a stable ref to the latest onError so the load effect can report
    // errors WITHOUT depending on onError's identity. The parent passes onError
    // inline (new function every render), so depending on it here would re-run
    // this effect on every parent re-render — e.g. when an error toast is shown
    // after a failed submit — wiping all the fields the user just typed.
    const onErrorRef = useRef(onError);
    useEffect(() => { onErrorRef.current = onError; }, [onError]);

    // Reset + load options ONLY when the modal transitions open. Depending on
    // isOpen alone guarantees fields are wiped on open and never on a re-render
    // caused by a submit error.
    useEffect(() => {
        if (!isOpen) return;
        let cancelled = false;
        setLoading(true);
        setSopId(''); setVariant('Compliance'); setMakerId(''); setCheckerId('');
        setGeography({ mode: 'locations', location_ids: [] }); setStartDate(''); setEndDate(''); vh.reset();
        (async () => {
            try {
                const [sopList, userList] = await Promise.all([
                    governanceProcessesService.getAll({ status: 'ACTIVE' }).catch(() => [] as any[]),
                    userService.getUsersDropdown(),
                ]);
                if (cancelled) return;
                // Dropdown must show approved + active SOPs only. Gate on maker-checker
                // workflow_state (NOT legacy process_status) — mirrors /processes/dropdown.
                setSops((sopList || []).filter((s: any) => s.workflow_state === 'approved').map((s: any) => ({
                    id: s.id,
                    process_code: s.process_code,
                    process_name: s.process_name,
                })));
                setUsers(userList.map(u => ({ id: u.id, name: u.name })));
            } catch (e: any) {
                if (!cancelled) onErrorRef.current(extractApiError(e, 'Failed to load options'));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!sopId || !variant || !makerId || !checkerId || !startDate || !endDate || !isGeographyComplete(geography)) {
            onError('SOP, variant, maker, checker, geography, start date and end date are required.');
            return;
        }
        if (endDate < startDate) { onError('End date must be on or after start date.'); return; }
        const ok = vh.validateAll([
            { name: 'start_date', code: 305, value: startDate, required: true },
            { name: 'end_date',   code: 305, value: endDate,   required: true },
            ...(startDate && endDate ? [{
                name: 'end_date', code: 601, value: 'x', required: true,
                additional: { from_date: startDate, to_date: endDate },
            }] : []),
        ]);
        if (!ok) return;
        setSaving(true);
        try {
            await sopProgramService.rollout(parseInt(sopId, 10), {
                variant,
                maker_id: parseInt(makerId, 10),
                checker_id: parseInt(checkerId, 10),
                geography,
                start_date: startDate,
                end_date: endDate,
            });
            onRolledOut();
            onClose();
        } catch (err: any) {
            onError(extractApiError(err, 'Failed to roll out program.'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="md" closeOnOverlayClick={false}>
            <Modal.Header>Roll Out PAT Program</Modal.Header>
            <Modal.Body>
                <form id="sop-rollout-form" data-testid="MOD_gov_program_rollout" onSubmit={submit} className="flex flex-col gap-y-4">
                    {makerId !== '' && makerId === checkerId && (
                        <div className="warning-banner" role="status"
                             data-testid="WRN_rollout_same_maker_checker">
                            Maker and checker are the same person. This assessment will be performed and approved by one individual — permitted, and recorded as self-reviewed.
                        </div>
                    )}
                    <FormControl.SearchSelect
                        label="SOP"
                        required
                        placeholder="Select SOP…"
                        options={sops.map(s => ({
                            value: s.id,
                            label: `${s.process_code ? `${s.process_code} — ` : ''}${s.process_name}`,
                        }))}
                        value={sopId}
                        onChange={v => setSopId(v != null ? String(v) : '')}
                        disabled={loading}
                        clearable
                        data-testid="SEL_gov_program_rollout_sop"
                    />
                    <div>
                        <label className="fg__label">Variant <span className="fg__required" aria-hidden="true">*</span></label>
                        <div className="flex gap-4 mt-1">
                            {(['Compliance', 'Design'] as SopVariant[]).map(v => (
                                <label key={v} className="flex items-center gap-1 text-sm cursor-pointer">
                                    <input
                                        type="radio"
                                        name="sop-variant"
                                        data-testid={`RAD_gov_program_rollout_variant_${v.toLowerCase()}`}
                                        checked={variant === v}
                                        onChange={() => setVariant(v)}
                                    />
                                    Test of {v}
                                </label>
                            ))}
                        </div>
                    </div>
                    <FormControl.SearchSelect
                        label="Maker (executor)"
                        required
                        placeholder="Select user…"
                        options={users.map(u => ({ value: u.id, label: u.name }))}
                        value={makerId}
                        onChange={v => setMakerId(v != null ? String(v) : '')}
                        disabled={loading}
                        clearable
                        data-testid="SEL_gov_program_rollout_maker"
                    />
                    <FormControl.SearchSelect
                        label="Checker (approver)"
                        required
                        placeholder="Select user…"
                        options={users.map(u => ({ value: u.id, label: u.name }))}
                        value={checkerId}
                        onChange={v => setCheckerId(v != null ? String(v) : '')}
                        disabled={loading}
                        clearable
                        data-testid="SEL_gov_program_rollout_checker"
                    />
                    <div>
                        <label className="fg__label">Geography <span className="fg__required" aria-hidden="true">*</span></label>
                        <GeographyPicker value={geography} onChange={setGeography} disabled={loading} />
                    </div>
                    <div className="grid grid-cols-2 gap-x-4">
                        <ValidatedDatePicker vh={vh} name="start_date" code={305} required label="Start Date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="INP_gov_program_rollout_start_date" />
                        <ValidatedDatePicker vh={vh} name="end_date" code={305} required label="End Date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="INP_gov_program_rollout_end_date" />
                    </div>
                </form>
            </Modal.Body>
            <Modal.Footer>
                <Button type="submit" form="sop-rollout-form" variant="fill" icon={Rocket} disabled={saving || loading} data-testid="BTN_gov_program_rollout_submit">
                    {saving ? 'Rolling out…' : 'Roll Out'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

const GovernancePrograms: React.FC = () => {
    const { formatDate } = useEntitySettings();
    const [rows, setRows] = useState<SopProgram[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [isRolloutOpen, setIsRolloutOpen] = useState(false);
    const { check } = usePermissions();
    const canModify = check('GOV_SOP_PROGRAMS', 'view+modify').allowed;
    const { allowed: canView, loading: viewLoading } = usePermission('GOV_SOP_PROGRAMS', 'view');
    const [toast, setToast] = useState<ToastMessage | null>(null);

    const refresh = useCallback(async () => {
        if (!canView) return;
        setLoading(true);
        try {
            const data = await sopProgramService.list({ search: search || undefined });
            setRows(data);
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to load programs') });
        } finally {
            setLoading(false);
        }
    }, [search, canView]);

    useEffect(() => { refresh(); }, [refresh]);
    useEffect(() => { setPage(1); }, [search, pageSize]);

    // Stable callbacks for the rollout modal. Inline arrows would change
    // identity on every render (e.g. when a toast is shown), which the modal
    // must not key effects on (see SopRolloutModal load effect).
    const handleClose = useCallback(() => setIsRolloutOpen(false), []);
    const handleRolledOut = useCallback(() => {
        setToast({ type: 'success', message: 'PAT Program rolled out.' });
        refresh();
    }, [refresh]);
    const handleError = useCallback((msg: string) => {
        setToast({ type: 'error', message: msg });
    }, []);

    const paged = rows.slice((page - 1) * pageSize, page * pageSize);

    if (!viewLoading && !canView) {
        return (
            <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
                <NoDataFound title="Not authorised" message="You do not have access to PAT programs." />
            </div>
        );
    }

    return (
        <div className="netbody-card flex flex-col h-full">
            <div className="netbody-header">
                <div className="flex items-center gap-2 ml-auto">
                    <FormControl.Input
                        type="text"
                        placeholder="Search programs…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        leadingIcon={<Search size={14} />}
                        className="filter-search"
                        data-testid="INP_gov_program_search"
                    />
                    {canModify && (
                        <Button variant="fill" icon={Plus} onClick={() => setIsRolloutOpen(true)} data-testid="BTN_gov_program_rollout_open">Roll Out</Button>
                    )}
                </div>
            </div>

            <div className="netbody-content">
                <div className="scroll-container flex-1 overflow-hidden">
                    <div className="table-list">
                        <div className="table-list-scroll">
                            <table className="table-list-table">
                                <thead>
                                    <tr>
                                        <th className="table-list-th table-list-cell--sno">S.No</th>
                                        <th className="table-list-th">Program</th>
                                        <th className="table-list-th">SOP</th>
                                        <th className="table-list-th">Variant</th>
                                        <th className="table-list-th">Geography</th>
                                        <th className="table-list-th">Window</th>
                                        <th className="table-list-th">Maker</th>
                                        <th className="table-list-th">Checker</th>
                                        <th className="table-list-th table-list-th--right">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading && <Skeleton.TableRows cols={[{ width: '3xs', align: 'center' }, 'md', 'sm', 'xs', 'sm', 'md', 'sm', 'sm', { width: 'pill', align: 'right' }]} />}
                                    {!loading && paged.length === 0 && (
                                        <tr>
                                            <td colSpan={9} className="table-list-cell">
                                                <NoDataFound
                                                    title={search ? 'No Matching Programs' : 'No PAT Programs Yet'}
                                                    message={search ? 'No programs match your search.' : 'Click "Roll Out" to launch a Process Assessment from any SOP.'}
                                                />
                                            </td>
                                        </tr>
                                    )}
                                    {!loading && paged.map((p, idx) => (
                                        <tr key={p.id} className="table-list-row">
                                            <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                                            <td className="table-list-cell">
                                                <span className="table-list-cell-title">{p.program_name}</span>
                                            </td>
                                            <td className="table-list-cell">{p.sop_code || p.sop_name || '—'}</td>
                                            <td className="table-list-cell">Test of {p.variant}</td>
                                            <td className="table-list-cell">{p.location_name || '—'}</td>
                                            <td className="table-list-cell">
                                                <span className="flex items-center gap-1 text-xs text-gray-600">
                                                    <Calendar size={12} className="text-gray-400" />
                                                    {formatDate(p.start_date)} → {formatDate(p.end_date)}
                                                </span>
                                            </td>
                                            <td className="table-list-cell">
                                                <span className="flex items-center gap-1 text-sm">
                                                    <User size={12} className="text-gray-400" />
                                                    {p.maker_name || '—'}
                                                </span>
                                            </td>
                                            <td className="table-list-cell">
                                                <span className="flex items-center gap-1 text-sm">
                                                    <User size={12} className="text-gray-400" />
                                                    {p.checker_name || '—'}
                                                </span>
                                            </td>
                                            <td className="table-list-cell table-list-cell--right">
                                                <span className={`status-badge sm ${statusBadgeVariant(p.status)}`}>
                                                    {p.status || 'Not-Started'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <Pagination
                            page={page}
                            pageSize={pageSize}
                            totalRecords={rows.length}
                            onPageChange={setPage}
                            onPageSizeChange={setPageSize}
                            recordLabel="programs"
                        />
                    </div>
                </div>
            </div>

            <SopRolloutModal
                isOpen={isRolloutOpen}
                onClose={handleClose}
                onRolledOut={handleRolledOut}
                onError={handleError}
            />

            <Toast toast={toast} onClose={() => setToast(null)} />
        </div>
    );
};

export default GovernancePrograms;
